import React, { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, Headphones, Ticket, XCircle, CheckCircle2 } from "lucide-react";
import { adminApi } from "../lib/api";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

function timeShort(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function TicketCard({ t, token, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const comment = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await adminApi.commentTicket(token, t.id, note.trim()); setNote(""); onChanged(); }
    catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  const resolve = async () => {
    setBusy(true);
    try { await adminApi.resolveTicket(token, t.id); toast({ title: "Ticket resolved & closed ✅" }); onChanged(); }
    catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-primary/25 bg-primary/5 px-3 py-2.5" data-testid={`admin-chat-ticket-${t.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <Ticket className="h-3.5 w-3.5 text-primary" />
        <span className="font-semibold text-primary">{t.ref}</span>
        <span className="text-muted-foreground">· {t.category} · {timeShort(t.created_at)}</span>
        <Button size="sm" variant="ghost" className="ml-auto h-7 px-2 text-[11px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" onClick={resolve} disabled={busy} data-testid={`admin-chat-ticket-resolve-${t.id}`}>
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolve & Close
        </Button>
      </div>
      <div className="text-sm mt-1 whitespace-pre-wrap break-words">{t.details}</div>
      {(t.comments || []).map((c, i) => (
        <div key={i} className="mt-1.5 text-xs rounded-lg bg-background/60 border border-border px-2.5 py-1.5"><span className="text-primary font-semibold">You · {timeShort(c.at)}</span> — {c.text}</div>
      ))}
      <div className="mt-2 flex items-center gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") comment(); }}
          placeholder="Comment on this ticket (user sees it in chat)…" data-testid={`admin-chat-ticket-comment-${t.id}`}
          className="flex-1 rounded-full bg-input/50 border border-input px-3.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-ring" />
        <Button size="sm" className="h-7 px-3 text-xs font-semibold" onClick={comment} disabled={busy || !note.trim()} data-testid={`admin-chat-ticket-comment-send-${t.id}`}>Send</Button>
      </div>
    </div>
  );
}

export default function AdminChat({ token }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // userId
  const [messages, setMessages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const scrollRef = useRef(null);
  const { toast } = useToast();

  const scrollBottom = () => requestAnimationFrame(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  });

  const loadThreads = async () => {
    try { setThreads(await adminApi.chatThreads(token)); } catch (e) { console.error("Load threads failed", e); }
  };

  const loadActive = async (userId, scroll = false) => {
    try {
      const [m, tk] = await Promise.all([adminApi.chatThread(token, userId), adminApi.chatUserTickets(token, userId)]);
      setMessages(m); setTickets(tk);
      if (scroll) scrollBottom();
    } catch (e) { console.error("Open thread failed", e); }
  };

  const openThread = async (userId) => {
    setActive(userId); setConfirmClose(false);
    await loadActive(userId, true);
    loadThreads();
  };

  useEffect(() => {
    loadThreads();
    const t = setInterval(() => { loadThreads(); if (active) loadActive(active); }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const reply = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setSending(true); setText("");
    try {
      const msg = await adminApi.chatReply(token, active, t);
      setMessages((prev) => [...prev, msg]); scrollBottom(); loadThreads();
    } catch (e) { console.error("Chat reply failed", e); }
    finally { setSending(false); }
  };

  const closeChat = async () => {
    if (!confirmClose) { setConfirmClose(true); return; }
    try {
      await adminApi.chatClose(token, active);
      toast({ title: "Chat closed", description: "Conversation and tickets removed for both sides." });
      setActive(null); setMessages([]); setTickets([]); setConfirmClose(false);
      loadThreads();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail }); }
  };

  const activeThread = threads.find((t) => t.userId === active);
  const onChanged = () => { loadActive(active, true); loadThreads(); };

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[600px]">
      <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-border font-display font-semibold text-sm flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" /> Conversations
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-border no-scrollbar">
          {threads.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="admin-chat-empty">No open conversations.</div>}
          {threads.map((t) => (
            <button key={t.userId} onClick={() => openThread(t.userId)} data-testid={`admin-chat-thread-${t.userId}`}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${active === t.userId ? "bg-primary/10" : "hover:bg-white/5"}`}>
              <div className="h-9 w-9 rounded-full bg-primary/15 grid place-items-center text-primary font-semibold shrink-0">
                {(t.userName || "U").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm truncate">{t.userName}</span>
                  <span className="flex items-center gap-1">
                    {t.tickets > 0 && <span className="h-5 min-w-[20px] px-1 rounded-full bg-primary/20 text-primary text-[11px] font-bold grid place-items-center" title="Open tickets"><Ticket className="h-3 w-3" /></span>}
                    {t.unread > 0 && <span className="h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold grid place-items-center">{t.unread}</span>}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground truncate">{t.lastMessage}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
        {!active ? (
          <div className="flex-1 grid place-items-center text-muted-foreground text-sm">
            <div className="text-center"><Headphones className="h-8 w-8 mx-auto mb-2 opacity-40" />Select a conversation to reply</div>
          </div>
        ) : (
          <>
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-3">
              <div className="font-display font-semibold text-sm">{activeThread?.userName || "User"}</div>
              <Button size="sm" variant={confirmClose ? "destructive" : "outline"} onClick={closeChat} onBlur={() => setConfirmClose(false)}
                className={confirmClose ? "" : "border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent"} data-testid="admin-chat-close">
                <XCircle className="h-4 w-4 mr-1" /> {confirmClose ? "Confirm — erase for both sides" : "Close chat"}
              </Button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
              {tickets.length > 0 && (
                <div className="space-y-2" data-testid="admin-chat-tickets">
                  {tickets.map((t) => <TicketCard key={t.id} t={t} token={token} onChanged={onChanged} />)}
                </div>
              )}
              {messages.length === 0 && tickets.length === 0 && <div className="text-center text-xs text-muted-foreground pt-6">No messages yet.</div>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === "admin" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                    {m.ticketRef && <div className={`text-[10px] font-semibold mb-0.5 ${m.sender === "admin" ? "text-primary-foreground/80" : "text-primary"}`}>Ticket comment</div>}
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{timeShort(m.at)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border p-3 flex items-center gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); reply(); } }}
                placeholder="Type your reply…" data-testid="admin-chat-input"
                className="flex-1 rounded-full bg-input/50 border border-input px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
              <Button size="icon" className="rounded-full shrink-0" onClick={reply} disabled={sending || !text.trim()} data-testid="admin-chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
